import { Injectable } from '@angular/core';
import { UserDto } from '../../../auth/user.dto';
import { ContractDto } from '../../../beneficiaries/beneficiary-folder/contract/contract.dto';
import { ContractEngagementTraceabilityStatus } from '../../traceability/contract-engagement-status.enum';
import { TPermission } from '../permissions';
import { IPermissionService } from './permission.service.interface';

type PermissionsByStatus = { [key in ContractEngagementTraceabilityStatus]: TPermission[] };
type ContractPermission = {
  ['ADMIN']: { state: PermissionsByStatus };
  ['STRUCTURE']: { state: PermissionsByStatus };
  ['DIRECTION+']: { state: PermissionsByStatus };
  ['TERRITORY']: { state: PermissionsByStatus };
};

@Injectable({
  providedIn: 'root',
})
export class ContractEngagmentPermissionService implements IPermissionService<ContractDto> {
  protected workflowAccess(user: UserDto): ContractPermission {
    return {
      ['ADMIN']: {
        state: {
          [ContractEngagementTraceabilityStatus.Creation]: [
            'CAN_EDIT',
            'CAN_DELETE',
            'CAN_GENERATE',
            'CE_CAN_BENEF_SIGN',
            'CE_REPRISE_CAN_BENEF_SIGN',
          ],
          [ContractEngagementTraceabilityStatus.Unvalidated]: [
            'CAN_DELETE',
            'CE_CAN_EDIT_ACTION',
            'CE_REPRISE_CAN_EDIT_ACTION',
            'CE_CAN_CLOSE',
            'CE_REPRISE_CAN_CLOSE',
          ],
          [ContractEngagementTraceabilityStatus.SignedBeneficiary]: [
            'CAN_DELETE',
            'CE_CAN_TERRITORY_VALIDATE',
            'CE_CAN_REJECT',
            'CE_CAN_ROLLBACK',
            'CE_REPRISE_CAN_TERRITORY_VALIDATE',
            'CE_REPRISE_CAN_REJECT',
            'CE_REPRISE_CAN_ROLLBACK',
          ],
          [ContractEngagementTraceabilityStatus.ValidatedTerritory]: [
            'CAN_DELETE',
            'CE_CAN_TERRITORY_SIGN',
            'CE_CAN_REJECT',
            'CE_REPRISE_CAN_DIRECTORY_VALIDATE',
            'CE_REPRISE_CAN_REJECT',
          ],
          [ContractEngagementTraceabilityStatus.ValidatedDie]: ['CAN_DELETE', 'CE_REPRISE_CAN_DIRECTORY_SIGN', 'CE_REPRISE_CAN_REJECT'],
          [ContractEngagementTraceabilityStatus.SignedTerritory]: ['CAN_DELETE', 'CE_CAN_CLOSE', 'CE_CAN_EDIT_ACTION'],
          [ContractEngagementTraceabilityStatus.SignedDie]: ['CAN_DELETE', 'CE_REPRISE_CAN_CLOSE', 'CE_REPRISE_CAN_EDIT_ACTION'],
          [ContractEngagementTraceabilityStatus.Done]: ['CAN_DELETE', 'CE_CAN_REOPEN', 'CE_REPRISE_CAN_REOPEN'],
        },
      },
      ['STRUCTURE']: {
        state: {
          [ContractEngagementTraceabilityStatus.Creation]: [
            'CAN_EDIT',
            'CAN_DELETE',
            'CAN_GENERATE',
            'CE_CAN_BENEF_SIGN',
            'CE_REPRISE_CAN_BENEF_SIGN',
          ],
          [ContractEngagementTraceabilityStatus.Unvalidated]: [
            'CAN_DELETE',
            'CE_CAN_EDIT_ACTION',
            'CE_REPRISE_CAN_EDIT_ACTION',
            'CE_CAN_CLOSE',
            'CE_REPRISE_CAN_CLOSE',
          ],
          [ContractEngagementTraceabilityStatus.SignedBeneficiary]: ['CAN_DELETE', 'CE_CAN_ROLLBACK', 'CE_REPRISE_CAN_ROLLBACK'],
          [ContractEngagementTraceabilityStatus.ValidatedTerritory]: ['CAN_DELETE'],
          [ContractEngagementTraceabilityStatus.SignedTerritory]: ['CE_CAN_CLOSE', 'CE_CAN_EDIT_ACTION'],
          [ContractEngagementTraceabilityStatus.ValidatedDie]: ['CAN_DELETE'],
          [ContractEngagementTraceabilityStatus.SignedDie]: ['CE_REPRISE_CAN_CLOSE', 'CE_REPRISE_CAN_EDIT_ACTION'],
          [ContractEngagementTraceabilityStatus.Done]: ['CE_CAN_REOPEN', 'CE_REPRISE_CAN_REOPEN'],
        },
      },
      ['DIRECTION+']: {
        state: {
          [ContractEngagementTraceabilityStatus.Creation]: ['CAN_EDIT', 'CAN_GENERATE', 'CE_CAN_BENEF_SIGN', 'CE_REPRISE_CAN_BENEF_SIGN'],
          [ContractEngagementTraceabilityStatus.ValidatedTerritory]: ['CE_REPRISE_CAN_DIRECTORY_VALIDATE', 'CE_REPRISE_CAN_REJECT'],
          [ContractEngagementTraceabilityStatus.ValidatedDie]: ['CE_REPRISE_CAN_DIRECTORY_SIGN', 'CE_REPRISE_CAN_REJECT'],
          [ContractEngagementTraceabilityStatus.SignedTerritory]: ['CE_CAN_CLOSE'],
          [ContractEngagementTraceabilityStatus.SignedBeneficiary]: undefined,
          [ContractEngagementTraceabilityStatus.Unvalidated]: undefined,
          [ContractEngagementTraceabilityStatus.SignedDie]: ['CE_REPRISE_CAN_CLOSE', 'CE_REPRISE_CAN_EDIT_ACTION'],
          [ContractEngagementTraceabilityStatus.Done]: undefined,
        },
      },
      ['TERRITORY']: {
        state: {
          [ContractEngagementTraceabilityStatus.Creation]: ['CAN_EDIT', 'CAN_GENERATE', 'CE_CAN_BENEF_SIGN', 'CE_REPRISE_CAN_BENEF_SIGN'],
          [ContractEngagementTraceabilityStatus.SignedBeneficiary]: [
            'CE_CAN_TERRITORY_VALIDATE',
            'CE_CAN_REJECT',
            'CE_REPRISE_CAN_TERRITORY_VALIDATE',
            'CE_REPRISE_CAN_REJECT',
          ],
          [ContractEngagementTraceabilityStatus.ValidatedTerritory]: ['CE_CAN_TERRITORY_SIGN', 'CE_CAN_REJECT'],
          [ContractEngagementTraceabilityStatus.ValidatedDie]: undefined,
          [ContractEngagementTraceabilityStatus.SignedTerritory]: ['CE_CAN_CLOSE', 'CE_REPRISE_CAN_EDIT_ACTION'],
          [ContractEngagementTraceabilityStatus.Unvalidated]: undefined,
          [ContractEngagementTraceabilityStatus.SignedDie]: ['CE_REPRISE_CAN_CLOSE'],
          [ContractEngagementTraceabilityStatus.Done]: undefined,
        },
      },
    };
  }

  public getPermissions(user: UserDto, subject: ContractDto): TPermission[] {
    return this.workflowAccess(user)[user.groups[0]]?.state[subject.status];
  }
}
